import { useAtom } from '@reatom/npm-react'
import React, { useMemo } from 'react'
import styled, { css } from 'styled-components'

import { DataTableContent } from './data-table-content'
import { DataTableContext } from './data-table-context'

import type {
  DataTableContentProps,
  DataTableVirtualization,
} from './data-table-content'
import type { Column } from '../..'
import type { DataTableModel } from '../atoms'
import type { ClassNameProps } from '@root/shared/react'
import type { TableVirtuosoHandle } from 'react-virtuoso'

export type DataTableProps<
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
> = {
  renderBackgroundLink?: (data: { data: D }) => React.ReactNode
  model: DataTableModel<D, ColumnId, object, Context>
  rowHeight?: number
  getRowStyle?: (row: D) => React.CSSProperties
  disableScroll?: boolean
  withBackToTop?: boolean
  overrideModelColumns?: ReadonlyArray<Column<D, ColumnId, Context>>
  onScroll?: (event: React.UIEvent<HTMLDivElement, UIEvent>) => void
  onRowClick?: (value: D, e: React.MouseEvent<HTMLDivElement>) => void
  emptyTitle?: string
  renderError?: ((e: unknown) => React.ReactNode) | undefined
  renderEmptyMessage?: () => React.ReactNode
  virtualization?: DataTableVirtualization
  stickyHeaderTop?: number
  increaseViewportBy?: number
  /**
   * @default false
   * If false index will not be passed to cell renderers, useful for high frequency lists to avoid re-rendering
   */
  disableIndex?: boolean
} & ClassNameProps &
  (Context extends object ? { context: Context } : { context?: undefined }) &
  React.HTMLAttributes<HTMLDivElement> & {
    virtuosoRef?: React.RefObject<TableVirtuosoHandle | null>
    scrollContainerRef?: React.RefObject<HTMLElement | null>
  } & Pick<
    DataTableContentProps<D, ColumnId, Context>,
    'renderRow' | 'customScrollParent' | 'floatingActions'
  >

export const DataTable = <
  D extends object,
  ColumnId extends string = string,
  Context extends object | undefined = undefined,
>({
  className,
  model,
  rowHeight = 30,
  getRowStyle,
  disableScroll = false,
  withBackToTop = true,
  floatingActions,
  onScroll,
  context,
  onRowClick,
  emptyTitle,
  renderError,
  renderEmptyMessage,
  virtualization = 'virtuoso',
  stickyHeaderTop,
  increaseViewportBy,
  virtuosoRef,
  scrollContainerRef,
  children,
  overrideModelColumns,
  renderBackgroundLink,
  renderRow,
  disableIndex = false,
  customScrollParent,
  ...divProps
}: DataTableProps<D, ColumnId, Context>) => {
  const [modelColumns] = useAtom(model.columns)
  const columns = useMemo(() => {
    return overrideModelColumns ? overrideModelColumns : modelColumns
  }, [overrideModelColumns, modelColumns])
  const minWidth = useMemo(
    () =>
      columns.reduce(
        (acc, column) =>
          acc +
          (column.hidden
            ? 0
            : // eslint-disable-next-line unicorn/no-nested-ternary
              typeof column.width === 'number'
              ? column.width
              : 0),
        0,
      ),
    [columns],
  )

  const contextValue = useMemo(
    () => ({
      disableIndex: disableIndex,
    }),
    [disableIndex],
  )

  return (
    <DataTableContext.Provider value={contextValue}>
      <Root
        className={className}
        data-table
        $virtualization={virtualization}
        {...divProps}
      >
        <DataTableContent<D, ColumnId, Context>
          virtualization={virtualization}
          stickyHeaderTop={stickyHeaderTop}
          increaseViewportBy={increaseViewportBy}
          renderBackgroundLink={renderBackgroundLink}
          renderRow={renderRow}
          minWidth={minWidth}
          virtuosoRef={virtuosoRef}
          scrollContainerRef={scrollContainerRef}
          disableScroll={disableScroll}
          withBackToTop={withBackToTop}
          floatingActions={floatingActions}
          renderError={renderError}
          renderEmptyMessage={renderEmptyMessage}
          columns={columns}
          model={model}
          rowHeight={rowHeight}
          getRowStyle={getRowStyle}
          onScroll={onScroll}
          context={context as Context}
          onRowClick={onRowClick}
          emptyTitle={emptyTitle}
          customScrollParent={customScrollParent}
        />
        {children}
      </Root>
    </DataTableContext.Provider>
  )
}

const Root = styled.div<{ $virtualization: DataTableVirtualization }>`
  --data-table-header-bg: var(--background-2);
  --data-table-header-color: var(--text-8);
  --data-table-header-color-hover: var(--text-12);
  --data-table-row-border-color: var(--backgroundA-2);
  --data-table-row-even-bg: var(--appearance-1);
  --data-table-row-even-bg: var(--appearance-1);
  --data-table-row-odd-bg: rgba(var(--background-2-rgb), 0.55);
  --data-table-header-padding-inline: 18px;
  --data-table-row-padding-inline: 18px;

  display: flex;
  flex-direction: column;
  position: relative;
  min-width: 0;
  z-index: 0;

  ${({ $virtualization }) =>
    $virtualization === 'virtuoso'
      ? css`
          overflow: hidden;
          min-height: 0;
          height: 100%;
        `
      : css`
          overflow: visible;
          display: grid;
          grid-template-rows: minmax(0, 1fr);
          min-height: 0;
          height: 100%;
        `}
`
